import { getPlayerDetails } from "@/sideEffects/api";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { ROUTES } from "@/constants/routeConstants";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { useTranslation } from "react-i18next";
import { LoadingSmall } from "@/components/LoadingSmall";
import { DeletePlayerButton } from "../DeletePlayerButton";
import { BaseFormLayout } from "@/pages/shared/BaseFormLayout";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { UpdatePlayerMetaForm } from "./UpdatePlayerMetaForm";
import { PlayerTags } from "./PlayerTags";
import { PlayerScheduleDetail } from "./PlayerScheduleDetail";
import { PlayerPublishButton } from "./PlayerPublishButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlayerInformationPage } from "./PlayerInformationPage";
import { PlayerSetupPage } from "./PlayerSetupPage";
import { PlayerStatsChart } from "./PlayerStatsChart";
import { useEffect, useState } from "react";

export const PlayerDetailPage = () => {
  const { t } = useTranslation();
  const [queryParams] = useSearchParams();
  const playerId = queryParams.get("Id");
  const activeFirm = useSelector(selectActiveFirm);
  const [skipSetup, setSkipSetup] = useState(false);

  const { data: playerDetails, isLoading } = useQuery({
    queryKey: ["getPlayerDetails", playerId],
    queryFn: () => getPlayerDetails(String(playerId), String(activeFirm?.firmGuid)),
    enabled: !!playerId,
    refetchInterval: 10 * 1000,
  });

  const playerDetailsData = playerDetails?.data;

  useBreadcrumbs([
    {
      name: t("entities.player"),
      path: ROUTES.PLAYER,
    },
    {
      name: playerDetailsData ? `${t("common.entityDetail", { name: playerDetailsData?.name })}` : "...",
      path: ROUTES.PLAYER_DETAIL,
    },
  ]);

  useEffect(() => {
    if (playerDetailsData?.deviceType) {
      setSkipSetup(true);
    }
  }, [playerDetailsData]);

  if (!playerDetailsData || isLoading) {
    return (
      <div className="flex justify-center items-center h-full p-16">
        <LoadingSmall />
      </div>
    );
  }

  if (!skipSetup) {
    return <PlayerSetupPage playerDetails={playerDetailsData} onClickSkip={() => setSkipSetup(true)} />;
  }

  return (
    <div className="flex flex-col gap-4 md:flex-row">
      <BaseFormLayout
        title={
          <>
            <div className="mb-2">{t("common.entityDetail", { name: playerDetailsData.name })}</div>
            <PlayerTags playerGuid={playerDetailsData.id} playerIntId={playerDetailsData.intId} />
          </>
        }
        rightContent={
          <div className="flex gap-2">
            <PlayerPublishButton
              playerGuid={String(playerDetailsData.id)}
              playerLastPublish={playerDetailsData.lastPublish}
            />
            <DeletePlayerButton playerId={playerDetailsData.id} />
          </div>
        }
      >
        <Tabs defaultValue="info">
          <TabsList>
            <TabsTrigger value="info">{t("common.info")}</TabsTrigger>
            <TabsTrigger value="config">{t("common.config")}</TabsTrigger>
            <TabsTrigger value="schedule">{t("schedule.preview")}</TabsTrigger>
            <TabsTrigger value="stats">{t("player.stats.title")}</TabsTrigger>
          </TabsList>
          <TabsContent value="info">
            <PlayerInformationPage playerDetails={playerDetailsData} />
          </TabsContent>
          <TabsContent value="config">
            <UpdatePlayerMetaForm playerDetails={playerDetailsData} />
          </TabsContent>
          <TabsContent value="schedule">
            <PlayerScheduleDetail playerDetails={playerDetailsData} />
          </TabsContent>
          <TabsContent value="stats">
            <PlayerStatsChart playerId={playerDetailsData.intId} />
          </TabsContent>
        </Tabs>
      </BaseFormLayout>
    </div>
  );
};
